import React from "react";
import ReactDOM from "react-dom/client";
import { createBrowserRouter, Navigate, RouterProvider } from "react-router-dom";
import { Provider } from "react-redux";
import { GoogleOAuthProvider } from "@react-oauth/google";
import store from "./redux/store";
import { QueryClientProvider } from "@tanstack/react-query";
import { queryClient } from "./services/queryClient";
import ErrorPage from "./error-page";
import Home from "./routes/home";
import SignIn from "./routes/signIn";
import SignUp from "./routes/signUp";
import Otp from "./routes/otp";
import Profile from "./routes/profile";
import ClubsRedirect from "./routes/ClubsRedirect";
import Artists from "./routes/artists";
import EventsRedirect from "./routes/EventsRedirect";
import EventsOrDetails from "./routes/EventsOrDetails";
import ClubDetails from "./routes/clubDetails";
import ArtistDetails from "./routes/artistDetails";
import EventDetails from "./routes/eventDetails";
import Contact from "./routes/contact";
import ForgotPassword from "./routes/forgotPassword";
import ResetPassword from "./routes/resetPassword";
import Favourites from "./routes/favorites";
import FollowedArtists from "./routes/followedArtists";
import Search from "./routes/search";
import EditProfile from "./routes/editProfile";
import AboutUs from "./routes/aboutus";
import Blog from "./routes/blog";
import BlogDetails from "./routes/blogDetails";
import HouseParties from "./routes/houseParties";
import "./index.css";
import "react-toastify/dist/ReactToastify.css";
import PrivacyPolicy from "./routes/privacyPolicy";
import TermsAndConditions from "./routes/termsAndConditions";
import NotFound from "./routes/notFound";
import GetApp from "./routes/getApp";
import Referral from "./routes/referral";
import ClubsOrDetails from "./routes/ClubsOrDetails";
import { LocationProvider } from "./context/LocationContext";

const router = createBrowserRouter([
  {
    path: "/",
    element: <Home />,
    errorElement: <ErrorPage />,
  },
  {
    path: "/signin",
    element: <SignIn />,
    errorElement: <ErrorPage />,
  },
  {
    path: "/signup",
    element: <SignUp />,
    errorElement: <ErrorPage />,
  },
  {
    path: "/otp",
    element: <Otp />,
    errorElement: <ErrorPage />,
  },
  {
    path: "/forgot-password",
    element: <ForgotPassword />,
    errorElement: <ErrorPage />,
  },
  {
    path: "/reset-password",
    element: <ResetPassword />,
    errorElement: <ErrorPage />,
  },
  {
    path: "/profile",
    element: <Profile />,
    errorElement: <ErrorPage />,
  },
  {
    path: "/edit-profile",
    element: <EditProfile />,
    errorElement: <ErrorPage />,
  },
  {
    path: "/clubs",
    element: <ClubsRedirect />,
    errorElement: <ErrorPage />,
  },
  {
    path: "/club/:city/:clubSlug",
    element: <ClubDetails />,
    errorElement: <ErrorPage />,
  },
  {
    path: "/clubs/:city/:clubSlug",
    element: <ClubDetails />,
    errorElement: <ErrorPage />,
  },
  {
    path: "/clubs/:city",
    element: <ClubsOrDetails />,
    errorElement: <ErrorPage />,
  },
  {
    path: "/artists",
    element: <Artists />,
    errorElement: <ErrorPage />,
  },
  {
    path: "/events",
    element: <EventsRedirect />,
    errorElement: <ErrorPage />,
  },
  {
    path: "/house-parties",
    element: <HouseParties />,
    errorElement: <ErrorPage />,
  },
  {
    path: "/artist/:artistId",
    element: <ArtistDetails />,
    errorElement: <ErrorPage />,
  },
  {
    path: "/artists/:artistId",
    element: <ArtistDetails />,
    errorElement: <ErrorPage />,
  },
  {
    path: "/event/:city/:eventSlug",
    element: <EventDetails />,
    errorElement: <ErrorPage />,
  },
  {
    path: "/event/:eventId",
    element: <EventDetails />,
    errorElement: <ErrorPage />,
  },
  {
    path: "/events/:cityOrId",
    element: <EventsOrDetails />,
    errorElement: <ErrorPage />,
  },
  {
    path: "/contact",
    element: <Contact />,
    errorElement: <ErrorPage />,
  },
  {
    path: "/favourites",
    element: <Favourites />,
    errorElement: <ErrorPage />,
  },
  {
    path: "/followed-artists",
    element: <FollowedArtists />,
    errorElement: <ErrorPage />,
  },
  {
    path: "/favourite-clubs",
    element: <Navigate to="/favourites?tab=clubs" replace />,
  },
  {
    path: "/favourite-events",
    element: <Navigate to="/favourites?tab=events" replace />,
  },
  {
    path: "/favourite-artists",
    element: <Navigate to="/favourites?tab=artists" replace />,
  },
  {
    path: "/search",
    element: <Search />,
    errorElement: <ErrorPage />,
  },
  {
    path: "/about",
    element: <AboutUs />,
    errorElement: <ErrorPage />,
  },
  {
    path: "/blog",
    element: <Blog />,
    errorElement: <ErrorPage />,
  },
  {
    path: "/blog/:slug",
    element: <BlogDetails />,
    errorElement: <ErrorPage />,
  },
  {
    path: "/privacy_policy",
    element: <PrivacyPolicy />,
    errorElement: <ErrorPage />,
  },
  {
    path: "/terms_conditions",
    element: <TermsAndConditions />,
    errorElement: <ErrorPage />,
  },
  {
    path: "/get",
    element: <GetApp />,
    errorElement: <ErrorPage />,
  },
  {
    path: "/get/",
    element: <GetApp />,
    errorElement: <ErrorPage />,
  },
  {
    path: "/get/*",
    element: <GetApp />,
    errorElement: <ErrorPage />,
  },
  {
    path: "/ref/:referralCode",
    element: <Referral />,
    errorElement: <ErrorPage />,
  },
  {
    path: "*",
    element: <NotFound />,
  },
]);

ReactDOM.createRoot(document.getElementById("root")).render(
  <React.StrictMode>
    <GoogleOAuthProvider
      clientId={
        import.meta.env.VITE_GOOGLE_CLIENT_ID ||
        "234696942853-e2tckulai268d5a1bgq3724fjicp5gii.apps.googleusercontent.com"
      }
    >
      <Provider store={store}>
        <QueryClientProvider client={queryClient}>
          <LocationProvider>
            <RouterProvider router={router} />
          </LocationProvider>
        </QueryClientProvider>
      </Provider>
    </GoogleOAuthProvider>
  </React.StrictMode>
);
